import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {View, Text, TouchableOpacity, ScrollView, Alert} from 'react-native';

import {text} from '../text';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {quantityInCart, addedToCartMessage} from '../utils';
import {hasPositiveWeight, formatCaloriesLabel} from '../utils/product';
import {useAppDispatch, useAppSelector} from '../hooks';
import {components} from '../components';
import type {
  RootStackParamList,
  ProductAddon,
  ProductAddonSelection,
} from '../types';
import {useAppNavigation} from '../hooks';
import {
  removeFromCart,
  addToCart,
  fullRemoveFromCart,
} from '../store/slices/cartSlice';
import {emitLocationPickerEvent} from '../utils/locationEvents';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {formatCurrency} from '../utils/currency';

type Props = NativeStackScreenProps<RootStackParamList, 'Product'>;

type AddonOptionState = {
  id: string;
  name: string;
  price: number;
  quantity: number;
};

type AddonState = {
  addonId: string;
  name: string;
  minUse: number;
  maxUse: number | null;
  options: AddonOptionState[];
};

const normalizeAddonState = (addons?: ProductAddon[]): AddonState[] => {
  if (!Array.isArray(addons)) {
    return [];
  }

  return addons.map((addon, addonIndex) => {
    const normalizedMaxUse =
      addon.maxUse && addon.maxUse > 0 ? addon.maxUse : null;

    return {
      addonId: addon.id || `addon-${addonIndex}`,
      name: addon.name,
      minUse: addon.minUse ?? 0,
      maxUse: normalizedMaxUse,
      options: addon.options.map((option, optionIndex) => ({
        id: option.id ?? `${addon.id}-${optionIndex}`,
        name: option.name,
        price: option.price,
        quantity: 0,
      })),
    };
  });
};

const calculateAddonQuantity = (addon: AddonState): number =>
  addon.options.reduce((total, option) => total + option.quantity, 0);

const buildCartItemId = (
  productId: string,
  selections: ProductAddonSelection[],
): string => {
  if (!Array.isArray(selections) || selections.length === 0) {
    return `${productId}::base`;
  }

  const selectionKey = selections
    .map((addon) => {
      const optionKey = addon.options
        .map((option) => `${option.id ?? option.name}x${option.quantity}`)
        .sort()
        .join('|');

      return `${addon.addonId}:${optionKey}`;
    })
    .sort()
    .join('||');

  return `${productId}::${selectionKey}`;
};

const Product: React.FC<Props> = ({route}): JSX.Element => {
  const {item} = route.params;
  const navigation = useAppNavigation();
  const dispatch = useAppDispatch();
  const locationState = useAppSelector((state) => state.locationSlice);
  const hasWeight = hasPositiveWeight(item.weight);
  const caloriesLabel = formatCaloriesLabel(item.calories);
  const hasCalories = typeof caloriesLabel === 'string' && caloriesLabel.length > 0;
  const hasAddons = Array.isArray(item.addons) && item.addons.length > 0;
  const [addonSelections, setAddonSelections] = useState<AddonState[]>(() =>
    hasAddons ? normalizeAddonState(item.addons) : [],
  );

  useEffect(() => {
    if (hasAddons) {
      setAddonSelections(normalizeAddonState(item.addons));
    } else {
      setAddonSelections([]);
    }
  }, [hasAddons, item.id]);

  const selectedAddons = useMemo<ProductAddonSelection[]>(() => {
    if (!hasAddons) {
      return item.selectedAddons ?? [];
    }

    return addonSelections
      .map((addon) => {
        const options = addon.options
          .filter((option) => option.quantity > 0)
          .map((option) => ({
            id: option.id,
            name: option.name,
            price: option.price,
            quantity: option.quantity,
            active: true,
          }));

        if (options.length === 0) {
          return null;
        }

        return {
          addonId: addon.addonId,
          name: addon.name,
          options,
        } as ProductAddonSelection;
      })
      .filter((addon): addon is ProductAddonSelection => Boolean(addon));
  }, [addonSelections, hasAddons, item.selectedAddons]);

  const addonsCost = useMemo(() => {
    if (!hasAddons) {
      return 0;
    }

    return addonSelections.reduce((total, addon) => {
      const addonTotal = addon.options.reduce(
        (sum, option) => sum + option.quantity * option.price,
        0,
      );
      return total + addonTotal;
    }, 0);
  }, [addonSelections, hasAddons]);

  const totalPrice = useMemo(
    () => Number(item.price ?? 0) + addonsCost,
    [addonsCost, item.price],
  );

  const cartItemPayload = useMemo(() => {
    if (!hasAddons) {
      return item;
    }

    const normalizedSelections = selectedAddons;
    return {
      ...item,
      price: totalPrice,
      basePrice: item.price,
      selectedAddons: normalizedSelections,
      cartItemId: buildCartItemId(item.id, normalizedSelections),
    };
  }, [hasAddons, item, selectedAddons, totalPrice]);

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const handleLocationRequest = () => {
    emitLocationPickerEvent();
  };

  const handleAddonIncrease = useCallback(
    (addonId: string, optionId: string) => {
      if (!hasAddons) {
        return;
      }

      setAddonSelections((previous) =>
        previous.map((addon) => {
          if (addon.addonId !== addonId) {
            return addon;
          }

          const maxAllowed =
            addon.maxUse !== null ? addon.maxUse : Number.POSITIVE_INFINITY;

          if (calculateAddonQuantity(addon) >= maxAllowed) {
            return addon;
          }

          return {
            ...addon,
            options: addon.options.map((option) => {
              if (option.id !== optionId) {
                return option;
              }

              return {...option, quantity: option.quantity + 1};
            }),
          };
        }),
      );
    },
    [hasAddons],
  );

  const handleAddonDecrease = useCallback(
    (addonId: string, optionId: string) => {
      if (!hasAddons) {
        return;
      }

      setAddonSelections((previous) =>
        previous.map((addon) => {
          if (addon.addonId !== addonId) {
            return addon;
          }

          return {
            ...addon,
            options: addon.options.map((option) => {
              if (option.id !== optionId) {
                return option;
              }

              if (option.quantity <= 0) {
                return option;
              }

              return {...option, quantity: option.quantity - 1};
            }),
          };
        }),
      );
    },
    [hasAddons],
  );

  const renderLocationRequired = () => {
    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          paddingHorizontal: 20,
        }}
        showsVerticalScrollIndicator={false}
      >
        <text.H3 style={{textAlign: 'center', color: theme.colors.textPrimary, marginBottom: 12}}>
          Veuillez entrez une adresse
        </text.H3>
        <text.T16 style={{textAlign: 'center', color: theme.colors.textMuted, marginBottom: 20}}>
          Sélectionnez une adresse pour afficher les détails du produit.
        </text.T16>
        <components.Button
          title='Choisir une adresse'
          onPress={handleLocationRequest}
          containerStyle={{alignSelf: 'stretch'}}
        />
      </ScrollView>
    );
  };

  const quantity = (quantityInCart(cartItemPayload) as number) || 0;

  const canAddToCart = useMemo(() => {
    if (!hasAddons) {
      return true;
    }

    return addonSelections.every((addon) => {
      const totalSelected = calculateAddonQuantity(addon);
      if (addon.minUse > 0 && totalSelected < addon.minUse) {
        return false;
      }

      if (addon.maxUse !== null && totalSelected > addon.maxUse) {
        return false;
      }

      return true;
    });
  }, [addonSelections, hasAddons]);

  const renderHeader = () => {
    return <components.Header basket={true} goBack={true} />;
  };

  const renderImage = () => {
    return (
      <View style={{marginBottom: 30, backgroundColor: theme.colors.backgroundPrimary}}>
        {item.image ? (
          <components.ImageBackground
            source={{uri: item.image}}
            resizeMode='cover'
            style={{
              height: 390,
              aspectRatio: 1,
              alignSelf: 'center',
            }}
          >
            <components.ProductInWishlist
              version={2}
              item={item}
              containerStyle={{
                position: 'absolute',
                right: 0,
                paddingHorizontal: 23,
                paddingTop: 32,
              }}
            />
            <components.ProductNew
              item={item}
              version={2}
              containerStyle={{
                position: 'absolute',
                top: 0,
                padding: 7,
                paddingHorizontal: 20,
                paddingTop: 20,
              }}
            />
          </components.ImageBackground>
        ) : (
          <View
            style={{
              height: 390,
              aspectRatio: 1,
              alignSelf: 'center',
              borderRadius: 10,
              backgroundColor: '#E9F3F6',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <components.ProductInWishlist
              version={2}
              item={item}
              containerStyle={{
                position: 'absolute',
                right: 0,
                paddingHorizontal: 23,
                paddingTop: 32,
              }}
            />
            <components.ProductNew
              item={item}
              version={2}
              containerStyle={{
                position: 'absolute',
                top: 0,
                padding: 7,
                paddingHorizontal: 20,
                paddingTop: 20,
              }}
            />
            <text.H3>{item.name}</text.H3>
          </View>
        )}
      </View>
    );
  };

  const renderDescription = () => {
    return (
      <View style={{paddingHorizontal: 20, marginBottom: 20}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <text.H3>{item.name}</text.H3>
          {hasWeight && hasCalories && (
            <text.T16>
              {item.weight} - {caloriesLabel}
            </text.T16>
          )}
          {hasWeight && !hasCalories && (
            <text.T16>{item.weight}</text.T16>
          )}
          {!hasWeight && hasCalories && (
            <text.T16>{caloriesLabel}</text.T16>
          )}
        </View>
        {!!item.description && <text.T16>{item.description}</text.T16>}
      </View>
    );
  };

  const renderAddons = () => {
    if (!hasAddons) {
      return null;
    }

    return (
      <View style={{paddingHorizontal: 20, marginBottom: 20}}>
        {addonSelections.map((addon) => {
          const totalSelected = calculateAddonQuantity(addon);
          const hasMaxLimit = addon.maxUse !== null;
          const selectionLabel = hasMaxLimit
            ? `${totalSelected}/${addon.maxUse}`
            : `${totalSelected}`;

          return (
            <View key={addon.addonId} style={{marginBottom: 18}}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  <text.H4>{addon.name}</text.H4>
                  {addon.minUse > 0 && (
                    <View
                      style={{
                        marginLeft: 8,
                        paddingHorizontal: 8,
                        paddingVertical: 2,
                        borderRadius: 6,
                        backgroundColor: 'lightgrey',
                      }}
                    >
                      <Text
                        style={{
                          ...theme.fonts.DMSans_500Medium,
                          fontSize: 10,
                          color: theme.colors.textPrimary,
                        }}
                      >
                        Required *
                      </Text>
                    </View>
                  )}
                </View>
                <View style={{alignItems: 'flex-end'}}>
                  <Text
                    style={{
                      ...theme.fonts.DMSans_400Regular,
                      fontSize: 10,
                      color: theme.colors.textMuted,
                    }}
                  >
                    Selected {selectionLabel}
                  </Text>
                </View>
              </View>
              {addon.options.map((option) => {
                const totalBeforeLimit = calculateAddonQuantity(addon);
                const maxAllowed =
                  addon.maxUse !== null
                    ? addon.maxUse
                    : Number.POSITIVE_INFINITY;
                const canIncrease = totalBeforeLimit < maxAllowed;
                const canDecrease = option.quantity > 0;
                const priceLabel =
                  option.price > 0
                    ? `+ ${formatCurrency(option.price)}`
                    : 'Included';

                return (
                  <View
                    key={option.id}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: theme.colors.backgroundPrimary,
                      borderRadius: 10,
                      paddingVertical: 12,
                      paddingHorizontal: 16,
                      marginBottom: 10,
                      borderWidth: 1,
                      borderColor: '#E6EEF5',
                    }}
                  >
                    <View style={{flex: 1, marginRight: 16}}>
                      <Text
                        style={{
                          ...theme.fonts.DMSans_500Medium,
                          fontSize: 14,
                          color: theme.colors.textPrimary,
                          marginBottom: 4,
                        }}
                      >
                        {option.name}
                      </Text>
                      <Text
                        style={{
                          ...theme.fonts.DMSans_400Regular,
                          fontSize: 12,
                          color:
                            option.price > 0
                              ? theme.colors.accentPrimary
                              : theme.colors.textMuted,
                        }}
                      >
                        {priceLabel}
                      </Text>
                    </View>
                    <View style={{flexDirection: 'row', alignItems: 'center'}}>
                      <TouchableOpacity
                        onPress={() =>
                          handleAddonDecrease(addon.addonId, option.id)
                        }
                        disabled={!canDecrease}
                        style={{opacity: canDecrease ? 1 : 0.3, padding: 6}}
                      >
                        <svg.DishMinusSvg color={theme.colors.textPrimary} />
                      </TouchableOpacity>
                      <Text
                        style={{
                          ...theme.fonts.DMSans_700Bold,
                          fontSize: 14,
                          color: theme.colors.textPrimary,
                          marginHorizontal: 12,
                        }}
                      >
                        {option.quantity}
                      </Text>
                      <TouchableOpacity
                        onPress={() =>
                          handleAddonIncrease(addon.addonId, option.id)
                        }
                        disabled={!canIncrease}
                        style={{opacity: canIncrease ? 1 : 0.3, padding: 6}}
                      >
                        <svg.DishPlusSvg color={theme.colors.textPrimary} />
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  };

  const renderAlert = () => {
    if (quantity > 0) {
      Alert.alert(
        'Item already in cart',
        'Do you want to add another one?',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'OK',
            onPress: () => {
              dispatch(fullRemoveFromCart(cartItemPayload));
              dispatch(addToCart(cartItemPayload));
              addedToCartMessage(item);
            },
          },
        ],
        {cancelable: false},
      );
      return;
    }
  };

  const renderButtons = () => {
    return (
      <View style={{paddingHorizontal: 20, paddingBottom: 10}}>
        <View
          style={{
            height: 60,
            backgroundColor: theme.colors.backgroundPrimary,
            marginBottom: 14,
            borderRadius: 10,
            paddingHorizontal: 20,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Text
            style={{
              ...theme.fonts.DMSans_700Bold,
              fontSize: 20,
              color: theme.colors.textPrimary,
            }}
          >
            {formatCurrency(totalPrice)}
          </Text>
          <View style={{flexDirection: 'row', alignItems: 'center'}}>
            <TouchableOpacity
              onPress={() => dispatch(removeFromCart(cartItemPayload))}
              disabled={quantity === 0}
              style={{opacity: quantity === 0 ? 0.3 : 1}}
            >
              <svg.DishMinusSvg color={theme.colors.textPrimary} />
            </TouchableOpacity>
            <View style={{paddingHorizontal: 24}}>
              <Text
                style={{
                  ...theme.fonts.DMSans_700Bold,
                  fontSize: 14,
                  color: theme.colors.textMuted,
                }}
              >
                {quantity}
              </Text>
            </View>

            <TouchableOpacity
              onPress={() => dispatch(addToCart(cartItemPayload))}
              disabled={!canAddToCart}
              style={{opacity: canAddToCart ? 1 : 0.3}}
            >
              <svg.DishPlusSvg color={theme.colors.textPrimary} />
            </TouchableOpacity>
          </View>
        </View>
        {hasAddons && !canAddToCart && (
          <Text
            style={{
              ...theme.fonts.DMSans_400Regular,
              fontSize: 12,
              color: theme.colors.accentPrimary,
              marginBottom: 10,
            }}
          >
            Please select the required options before adding to cart.
          </Text>
        )}
        <components.Button
          title='+ Add to cart'
          containerStyle={{marginBottom: 14}}
          disabled={!canAddToCart}
          onPress={() => {
            if (quantity > 0) {
              renderAlert();
              return;
            }
            dispatch(addToCart(cartItemPayload));
            addedToCartMessage(item);
          }}
        />
        <components.Button
          title='Leave a review'
          transparent={true}
          containerStyle={{
            backgroundColor: theme.colors.backgroundPrimary,
          }}
          onPress={() => {
            navigation.navigate('LeaveAReview');
          }}
        />
      </View>
    );
  };

  const renderContent = () => {
    if (!locationState.isAddressConfirmed) {
      return renderLocationRequired();
    }

    return (
      <ScrollView
        contentContainerStyle={{flexGrow: 1}}
        showsVerticalScrollIndicator={false}
      >
        {renderImage()}
        {renderDescription()}
        {renderAddons()}
        {renderButtons()}
      </ScrollView>
    );
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderContent()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Product;
